import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import {
  Users,
  Plus,
  Edit,
  Trash2,
  Star,
  Eye,
  EyeOff,
  MapPin,
  Briefcase,
  X,
  FolderOpen,
  Image as ImageIcon
} from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import AdminMediaLibrary from './AdminMediaLibrary';
import { toRelativeMediaPath } from '../utils/mediaUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminCustomerCases = () => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCase, setEditingCase] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    customer_name: '',
    industry: '',
    location: '',
    service_used: '',
    quote: '',
    author_name: '',
    author_title: '',
    rating: 5,
    featured_image: '',
    image_before: '',
    image_after: '',
    challenge: '',
    solution: '',
    results: [],
    meta_title: '',
    meta_description: '',
    published: false,
    featured: false
  });
  const [resultsInput, setResultsInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [activeImageField, setActiveImageField] = useState(null); // 'featured', 'before', 'after'
  
  const { toast } = useToast();
  const { token } = useAuth();

  // Handle media selection from library
  const handleMediaSelect = (media) => {
    const imageUrl = toRelativeMediaPath(media.versions?.medium || media.url || `/api/media/${media.filename}`);
    
    if (activeImageField === 'featured') {
      setFormData({ ...formData, featured_image: imageUrl });
    } else if (activeImageField === 'before') {
      setFormData({ ...formData, image_before: imageUrl });
    } else if (activeImageField === 'after') {
      setFormData({ ...formData, image_after: imageUrl });
    }
    
    setShowMediaPicker(false);
    toast({
      title: 'Bilde valgt',
      description: `${media.title || media.filename} er valgt`
    });
  };

  const openMediaPicker = (field) => {
    setActiveImageField(field);
    setShowMediaPicker(true);
  };

  useEffect(() => {
    fetchCases();
  }, []);

  const fetchCases = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/cases?published_only=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCases(response.data);
    } catch (error) {
      console.error('Error fetching cases:', error);
      toast({
        title: "Feil",
        description: "Kunne ikke laste kundecase",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = async (caseItem = null) => {
    if (caseItem) {
      // Fetch full case data
      try {
        const response = await axios.get(`${API}/cases/${caseItem.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const fullCase = response.data;
        setEditingCase(fullCase);
        setFormData({
          title: fullCase.title || '',
          slug: fullCase.slug || '',
          customer_name: fullCase.customer_name || '',
          industry: fullCase.industry || '',
          location: fullCase.location || '',
          service_used: fullCase.service_used || '',
          quote: fullCase.quote || '',
          author_name: fullCase.author_name || '',
          author_title: fullCase.author_title || '',
          rating: fullCase.rating || 5,
          featured_image: fullCase.featured_image || '',
          image_before: fullCase.image_before || '',
          image_after: fullCase.image_after || '',
          challenge: fullCase.challenge || '',
          solution: fullCase.solution || '',
          results: fullCase.results || [],
          meta_title: fullCase.meta_title || '',
          meta_description: fullCase.meta_description || '',
          published: fullCase.published || false,
          featured: fullCase.featured || false
        });
        setResultsInput((fullCase.results || []).join('\n'));
      } catch (error) {
        console.error('Error fetching case:', error);
      }
    } else {
      setEditingCase(null);
      setFormData({
        title: '',
        slug: '',
        customer_name: '',
        industry: '',
        location: '',
        service_used: '',
        quote: '',
        author_name: '',
        author_title: '',
        rating: 5,
        featured_image: '',
        image_before: '',
        image_after: '',
        challenge: '',
        solution: '',
        results: [],
        meta_title: '',
        meta_description: '',
        published: false,
        featured: false
      });
      setResultsInput('');
    }
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.title || !formData.customer_name) {
      toast({
        title: "Feil",
        description: "Tittel og kundenavn er påkrevd",
        variant: "destructive"
      });
      return;
    }

    try {
      setSaving(true);
      
      // Parse results from textarea
      const resultsArray = resultsInput
        .split('\n')
        .map(r => r.trim())
        .filter(r => r.length > 0);
      
      const dataToSave = {
        ...formData,
        results: resultsArray
      };
      
      if (editingCase) {
        await axios.put(`${API}/cases/${editingCase.id}`, dataToSave, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({
          title: "Oppdatert! ✅",
          description: `${formData.title} er oppdatert`
        });
      } else {
        await axios.post(`${API}/cases`, dataToSave, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({
          title: "Opprettet! ✅",
          description: `${formData.title} er opprettet`
        });
      }
      
      setIsModalOpen(false);
      await fetchCases();
    } catch (error) {
      console.error('Error saving case:', error);
      toast({
        title: "Feil",
        description: error.response?.data?.detail || "Kunne ikke lagre kundecase",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (caseId, caseTitle) => {
    if (!window.confirm(`Er du sikker på at du vil slette "${caseTitle}"?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/cases/${caseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Slettet! ✅",
        description: `${caseTitle} er fjernet`
      });
      
      await fetchCases();
    } catch (error) {
      console.error('Error deleting case:', error);
      toast({
        title: "Feil",
        description: "Kunne ikke slette kundecase",
        variant: "destructive"
      });
    }
  };

  const handleTogglePublished = async (caseItem) => {
    try {
      await axios.put(`${API}/cases/${caseItem.id}`, {
        published: !caseItem.published
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: caseItem.published ? "Avpublisert" : "Publisert",
        description: `${caseItem.title} er nå ${caseItem.published ? 'skjult' : 'synlig'}`
      });
      
      await fetchCases();
    } catch (error) {
      console.error('Error toggling case:', error);
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'}`}
      />
    ));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-white text-xl">Laster kundecase...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2">
            <span className="bg-gradient-to-r from-yellow-400 to-red-500 bg-clip-text text-transparent">
              Kundreferanser
            </span>
          </h2>
          <p className="text-gray-400">Administrer kundesuksesshistorier som vises på Referanser-siden</p>
        </div>
        <Button
          onClick={() => handleOpenModal()}
          className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          Ny kundecase
        </Button>
      </div>

      {/* Cases List */}
      <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-yellow-400" />
            Alle kundecase ({cases.length})
          </CardTitle>
          <CardDescription className="text-gray-400">
            Klikk på et case for å redigere. Bruk øyet for å publisere/avpublisere.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {cases.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg">Ingen kundecase ennå</p>
              <p className="text-sm mt-2">Klikk "Ny kundecase" for å komme i gang</p>
            </div>
          ) : (
            <div className="space-y-3">
              {cases.map((caseItem) => (
                <div
                  key={caseItem.id}
                  className={`p-4 rounded-lg border transition-all ${
                    caseItem.published 
                      ? 'bg-gray-900/50 border-gray-700 hover:border-gray-600' 
                      : 'bg-gray-900/30 border-gray-800 opacity-70'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Featured Image */}
                    <div className="w-20 h-16 bg-gray-800 rounded overflow-hidden flex-shrink-0">
                      {caseItem.featured_image && typeof caseItem.featured_image === 'string' && caseItem.featured_image.length > 0 ? (
                        <img
                          src={caseItem.featured_image.startsWith('http') ? caseItem.featured_image : `${BACKEND_URL}${caseItem.featured_image}`}
                          alt={caseItem.customer_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Users className="w-8 h-8 text-gray-600" />
                        </div>
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-white font-medium truncate">{caseItem.title}</h3>
                        {caseItem.featured && (
                          <Badge className="bg-yellow-600/20 text-yellow-400 border-yellow-600/50 text-xs">
                            Utvalgt
                          </Badge>
                        )}
                      </div>
                      <p className="text-gray-400 text-sm mb-2">{caseItem.customer_name}</p>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                        {caseItem.industry && (
                          <span className="flex items-center gap-1">
                            <Briefcase className="w-3 h-3" />
                            {caseItem.industry}
                          </span>
                        )}
                        {caseItem.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {caseItem.location}
                          </span>
                        )}
                        <div className="flex">{renderStars(caseItem.rating)}</div>
                      </div>
                    </div>
                    
                    {/* Status Badge */}
                    <Badge 
                      className={caseItem.published 
                        ? "bg-green-600/20 text-green-400 border-green-600/50" 
                        : "bg-gray-600/20 text-gray-400 border-gray-600/50"
                      }
                    >
                      {caseItem.published ? 'Publisert' : 'Utkast'}
                    </Badge>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className={caseItem.published 
                          ? "border-yellow-600 text-yellow-400" 
                          : "border-green-600 text-green-400"
                        }
                        onClick={() => handleTogglePublished(caseItem)}
                        title={caseItem.published ? 'Avpubliser' : 'Publiser'}
                      >
                        {caseItem.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-blue-600 text-blue-400"
                        onClick={() => handleOpenModal(caseItem)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-red-600 text-red-400 hover:bg-red-600/20"
                        onClick={() => handleDelete(caseItem.id, caseItem.title)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCase ? 'Rediger kundecase' : 'Ny kundecase'}</DialogTitle>
            <DialogDescription className="text-gray-400">
              Fyll ut informasjon om kundens suksesshistorie
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-gray-300 mb-2 block">Tittel *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="F.eks. 30% redusert nedetid for transportflåten"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              
              <div>
                <Label className="text-gray-300 mb-2 block">Kundenavn *</Label>
                <Input
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  placeholder="F.eks. Kongsvinger Transport AS"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              
              <div>
                <Label className="text-gray-300 mb-2 block">Slug (URL)</Label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="Genereres automatisk hvis tom"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              
              <div>
                <Label className="text-gray-300 mb-2 block">Bransje</Label>
                <Input
                  value={formData.industry}
                  onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                  placeholder="F.eks. Transport og logistikk"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              
              <div>
                <Label className="text-gray-300 mb-2 block">Sted</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="F.eks. Kongsvinger"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              
              <div>
                <Label className="text-gray-300 mb-2 block">Tjeneste brukt</Label>
                <Input
                  value={formData.service_used}
                  onChange={(e) => setFormData({ ...formData, service_used: e.target.value })}
                  placeholder="F.eks. EU-kontroll tungbil"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              
              <div>
                <Label className="text-gray-300 mb-2 block">Vurdering</Label>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setFormData({ ...formData, rating: star })}
                      className="focus:outline-none"
                    >
                      <Star
                        className={`w-6 h-6 ${
                          star <= formData.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Quote */}
            <div>
              <Label className="text-gray-300 mb-2 block">Sitat (The Socratic Quote)</Label>
              <Textarea
                value={formData.quote}
                onChange={(e) => setFormData({ ...formData, quote: e.target.value })}
                placeholder="Et kort, impactfullt sitat fra kunden..."
                className="bg-gray-800 border-gray-700 text-white min-h-[100px]"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300 mb-2 block">Sitatforfatter</Label>
                <Input
                  value={formData.author_name}
                  onChange={(e) => setFormData({ ...formData, author_name: e.target.value })}
                  placeholder="F.eks. Erik Haugen"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-300 mb-2 block">Forfatterens stilling</Label>
                <Input
                  value={formData.author_title}
                  onChange={(e) => setFormData({ ...formData, author_title: e.target.value })}
                  placeholder="F.eks. Logistikksjef"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
            </div>
            
            {/* Case Details */}
            <div className="border-t border-gray-700 pt-4">
              <h4 className="text-white font-medium mb-3">Case-detaljer (vises i utvidet visning)</h4>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-300 mb-2 block">Utfordringen</Label>
                  <Textarea
                    value={formData.challenge}
                    onChange={(e) => setFormData({ ...formData, challenge: e.target.value })}
                    placeholder="Beskriv utfordringen kunden hadde..."
                    className="bg-gray-800 border-gray-700 text-white min-h-[80px]"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-300 mb-2 block">Løsningen</Label>
                  <Textarea
                    value={formData.solution}
                    onChange={(e) => setFormData({ ...formData, solution: e.target.value })}
                    placeholder="Beskriv hvordan Sand-tec løste utfordringen..."
                    className="bg-gray-800 border-gray-700 text-white min-h-[80px]"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-300 mb-2 block">Resultater (én per linje)</Label>
                  <Textarea
                    value={resultsInput}
                    onChange={(e) => setResultsInput(e.target.value)}
                    placeholder="30% redusert nedetid&#10;Reisetid kuttet fra 4 timer til 40 minutter&#10;Årlig besparelse på over 150.000 kr"
                    className="bg-gray-800 border-gray-700 text-white min-h-[100px]"
                  />
                </div>
              </div>
            </div>
            
            {/* Images */}
            <div className="border-t border-gray-700 pt-4">
              <h4 className="text-white font-medium mb-3">Bilder</h4>
              
              <div className="space-y-4">
                {/* Featured Image */}
                <div>
                  <Label className="text-gray-300 mb-2 block">Hovedbilde</Label>
                  <div className="flex gap-2">
                    <Input
                      value={formData.featured_image}
                      onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                      placeholder="Lim inn URL eller velg fra mediebibliotek"
                      className="flex-1 bg-gray-800 border-gray-700 text-white"
                      maxLength={500}
                    />
                    <Button
                      type="button"
                      onClick={() => openMediaPicker('featured')}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      <FolderOpen className="w-4 h-4 mr-2" />
                      Velg
                    </Button>
                  </div>
                  {formData.featured_image && typeof formData.featured_image === 'string' && formData.featured_image.length > 0 && (
                    <div className="flex items-center gap-3 mt-2 p-2 bg-gray-800/50 rounded-lg">
                      <img 
                        src={formData.featured_image.startsWith('http') ? formData.featured_image : `${BACKEND_URL}${formData.featured_image}`} 
                        alt="Preview" 
                        className="w-20 h-14 object-cover rounded"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, featured_image: '' })}
                        className="text-xs text-red-400 hover:text-red-300"
                      >
                        Fjern
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Before/After Images */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300 mb-2 block">Før-bilde</Label>
                    <div className="flex gap-2">
                      <Input
                        value={formData.image_before}
                        onChange={(e) => setFormData({ ...formData, image_before: e.target.value })}
                        placeholder="URL eller velg bilde"
                        className="flex-1 bg-gray-800 border-gray-700 text-white"
                        maxLength={500}
                      />
                      <Button
                        type="button"
                        onClick={() => openMediaPicker('before')}
                        className="bg-purple-600 hover:bg-purple-700 text-white px-3"
                      >
                        <FolderOpen className="w-4 h-4" />
                      </Button>
                    </div>
                    {formData.image_before && typeof formData.image_before === 'string' && formData.image_before.length > 0 && (
                      <div className="flex items-center gap-2 mt-2">
                        <img 
                          src={formData.image_before.startsWith('http') ? formData.image_before : `${BACKEND_URL}${formData.image_before}`} 
                          alt="Before" 
                          className="w-16 h-12 object-cover rounded"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, image_before: '' })}
                          className="text-xs text-red-400"
                        >
                          Fjern
                        </button>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-gray-300 mb-2 block">Etter-bilde</Label>
                    <div className="flex gap-2">
                      <Input
                        value={formData.image_after}
                        onChange={(e) => setFormData({ ...formData, image_after: e.target.value })}
                        placeholder="URL eller velg bilde"
                        className="flex-1 bg-gray-800 border-gray-700 text-white"
                        maxLength={500}
                      />
                      <Button
                        type="button"
                        onClick={() => openMediaPicker('after')}
                        className="bg-purple-600 hover:bg-purple-700 text-white px-3"
                      >
                        <FolderOpen className="w-4 h-4" />
                      </Button>
                    </div>
                    {formData.image_after && typeof formData.image_after === 'string' && formData.image_after.length > 0 && (
                      <div className="flex items-center gap-2 mt-2">
                        <img 
                          src={formData.image_after.startsWith('http') ? formData.image_after : `${BACKEND_URL}${formData.image_after}`} 
                          alt="After" 
                          className="w-16 h-12 object-cover rounded"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, image_after: '' })}
                          className="text-xs text-red-400"
                        >
                          Fjern
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* SEO */}
            <div className="border-t border-gray-700 pt-4">
              <h4 className="text-white font-medium mb-3">SEO</h4>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-300 mb-2 block">Meta-tittel</Label>
                  <Input
                    value={formData.meta_title}
                    onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                    placeholder="Bruker hovedtittel hvis tom"
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300 mb-2 block">Meta-beskrivelse</Label>
                  <Textarea
                    value={formData.meta_description}
                    onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                    placeholder="Kort beskrivelse for søkemotorer (150-160 tegn)"
                    className="bg-gray-800 border-gray-700 text-white min-h-[60px]"
                  />
                </div>
              </div>
            </div>
            
            {/* Status */}
            <div className="flex items-center gap-6 border-t border-gray-700 pt-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="published"
                  checked={formData.published}
                  onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-700 bg-gray-900"
                />
                <Label htmlFor="published" className="text-gray-300 cursor-pointer">
                  Publisert
                </Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="featured"
                  checked={formData.featured}
                  onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-700 bg-gray-900"
                />
                <Label htmlFor="featured" className="text-gray-300 cursor-pointer">
                  Utvalgt (vises på startsiden)
                </Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsModalOpen(false)}
              className="border-gray-600 text-gray-300"
            >
              Avbryt
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-gradient-to-r from-red-600 to-yellow-500"
            >
              {saving ? 'Lagrer...' : editingCase ? 'Oppdater' : 'Opprett'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Media Picker Dialog */}
      <Dialog open={showMediaPicker} onOpenChange={setShowMediaPicker}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-gray-900 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-purple-400" />
              <span>Velg bilde fra mediebibliotek</span>
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Klikk på et bilde for å velge det
            </DialogDescription>
          </DialogHeader>
          
          <AdminMediaLibrary 
            pickerMode={true} 
            onSelect={handleMediaSelect}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCustomerCases;
